/* eslint-disable react/prop-types */
import classNames from 'classnames';
import parse from 'html-react-parser';
import loadable from '@loadable/component';
import { connect } from 'react-redux';
import { useIntl, FormattedMessage } from 'react-intl';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import SDPHeader from './SdpHeader';
import SDPTopBrands from './SdpTopBrands';
import SDPAssortment from './SdpAssortment';
import SDPCertificates from './SDPCertificates';
import SDPPayment from './SDPPayment';
//todo head: contentApi.getShopInfo
// utils
import getCDNImage from '../../utils/getCDNImage';
import { VIEW_OPTIONS_TILES } from '../../components/SearchResultsPage/SRP.definitions';
import { ratingMicrodata } from 'Features/reviews/ReviewsComponent/reviewUtils';

import styles from './ShopDetailsPage.module.scss';
import { shopApi } from '../../api';
import { clipTextEllipsis } from '../../utils/textEllipsis';
import loadVisibleComponent from '../../utils/loadVisibleComponent';
import SimilarShops from '../../components/SimilarShops/SimilarShops';
import { useAuth0Wrapper } from '../../utils/auth0';
import { getForwardedHeaders } from '../../utils/getForwardedHeaders';

import { ReactComponent as InfoIcon } from '../../images/icons/shop-info.svg';
import { ReactComponent as ReturnIcon } from '../../images/icons/return.svg';
import { ReactComponent as ArrowDownIcon } from '../../images/icons/arrowDown.svg';
import { SwitchableFeature } from '../../features/SwitchableFeature/SwitchableFeature';
import { CART } from '../../features/SwitchableFeature/SwitchableFeature.definitions';
import ReturnPolicyModal from './ReturnPolicyModal/ReturnPolicyModal';
import { PRODUCT_SHOP_DEALS } from '../../components/Product/ProductCardOriginType.def';
import { getPagePath } from '../../utils/appRoutes';
import { ARD_PROFILE_ORDERS_N_RETURNS } from '../../utils/appRoutes.definitions';
// shared components
const ShopProducts = loadable(() => import('./ShopProducts'));
const TopCategories = loadVisibleComponent(() => import('./TopCategories'));
const StickyHeader = loadable(() => import('Components/StickyHeader/StickyHeader'));
const Product = loadable(() => import('../../components/Product/Product'));
const LoginModal = loadable(() => import('../../components/LoginModal/LoginModal'));
const Breadcrumb = loadable(() => import('../../components/Breadcrumb/Breadcrumb'));
const RecentlyViewedProducts = loadVisibleComponent(() =>
  import('../../components/RecentlyViewedProducts/RecentlyViewedProducts')
);
const NewsletterSection = loadVisibleComponent(() => import('../../components/NewsletterSection/NewsletterSection'));
const Footer = loadVisibleComponent(() => import('../../components/Footer/Footer'));
const ReviewsComponent = loadVisibleComponent(() => import('Features/reviews/ReviewsComponent'));

// local components
// const ShopMasonary = loadable(() => import('./ShopMasonary/ShopMasonary'));

const ShopDetailsPage = ({ config, intl, profile, clickout }) => {
  const { shopWebsite } = useParams();
  const { shopInfo } = useLoaderData();
  const navigate = useNavigate();
  const { tokenClaims } = useAuth0Wrapper();
  const authToken = tokenClaims?.__raw;

  const [openOverlay, setOpenOverlay] = useState(false);
  const form = useRef(null);

  const [openReadMore, setOpenReadMore] = useState(false);
  const [hasReadMore, setHasReadMore] = useState(false);
  const highlightRef = useRef(null);

  const intlFull = useIntl();
  const [openReturnPolicy, setOpenReturnPolicy] = useState(false);

  useEffect(() => {
    if (openReadMore) {
      setOpenReadMore(false);
    }
  }, [shopInfo]);

  useEffect(() => {
    if (!openReadMore) {
      const hasReadMore = highlightRef?.current?.offsetHeight < highlightRef?.current?.scrollHeight;
      setHasReadMore(hasReadMore);
    }
  }, [openReadMore, shopInfo]);

  const openHightLightOpen = () => {
    setOpenReadMore(true);
  };

  const closeModal = () => {
    setOpenReturnPolicy(false);
  };

  const showOverlay = () => setOpenOverlay(true);

  const addReviewRequest = useCallback(
    async (formElements, rating) => {
      const payload = {
        shop_id: `${shopInfo?.shop_id}`, //value should be a string, even if contains number
        rating,
        title: formElements.title.value.trim(),
        text: formElements.content.value.trim(),
      };
      let submitResp = null;
      try {
        const resp = await shopApi.addReview({
          body: JSON.stringify(payload),
          method: 'POST',
          headers: { ...getForwardedHeaders(), ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        });
        submitResp = resp;
      } catch (err) {
        console.error(err);
        submitResp = { ok: false };
      }

      return submitResp;
    },
    [authToken, shopInfo?.shop_id]
  );

  const topDiscounts = shopInfo?.tops?.discounts?.products?.hits?.hits?.map(({ _source }) => _source);

  const storeSchemaBrands = shopInfo?.tops?.brands?.buckets
    ?.map(
      ({ key }) => `{
      "@type": "Brand",
      "name": "${key}"
    }`
    )
    ?.join(',');
  const storeSchemaBrandsString = storeSchemaBrands ? `[${storeSchemaBrands}]` : 'null';
  const metaTitle = shopInfo?.shop_meta?.yoast_wpseo_title || shopInfo?.shop_name;
  const metaDescription = shopInfo?.shop_meta?.yoast_wpseo_metadesc;
  return shopInfo ? (
    <>
      <Helmet>
        {shopInfo?.sdp_path && config.baseUrl && <link rel="canonical" href={config.baseUrl + shopInfo?.sdp_path} />}
        <title>{clipTextEllipsis(metaTitle, 60)}</title>
        <meta name="twitter:title" content={clipTextEllipsis(metaTitle, 70)} />
        <meta property="og:title" content={clipTextEllipsis(metaTitle, 50)} />
        {shopInfo?.sdp_path && <meta property="og:url" content={shopInfo?.sdp_path} />}
      </Helmet>
      {metaDescription && (
        <Helmet>
          <meta name="description" content={metaDescription} />
          <meta name="twitter:description" content={clipTextEllipsis(metaDescription, 200)} />
          <meta property="og:description" content={clipTextEllipsis(metaDescription, 160)} />
        </Helmet>
      )}
      <StickyHeader />
      <div className={styles.shopDetail} data-testid="shopDetail" id="readyForCrawler">
        <div className="container">
          <div className={classNames(styles.breadcrumb, 'row')}>
            {/* This is only used on desktop */}
            <Breadcrumb
              items={[
                {
                  url: `/${config.cmsSlugs.webshops}/`,
                  slug: 'allShops',
                  text: `${intl.messages['common.all']} ${intl.messages['common.shops']}`,
                },
                { slug: 'shopWebsite', text: shopInfo.shop_name },
              ]}
            />
          </div>
        </div>
        <main className={classNames(styles.shopContainer, 'container')}>
          <div
            ref={highlightRef}
            className={classNames(styles.highlights, { [styles.highlightsOpen]: hasReadMore && openReadMore })}
          >
            <SDPHeader
              shopInfo={shopInfo}
              clickout={clickout}
              profile={profile}
              setOpenOverlay={setOpenOverlay}
              form={form}
            />
            <hr />
            <SDPTopBrands shopInfo={shopInfo} config={config} intlFull={intlFull} clickout={clickout} />
            <SDPAssortment shopInfo={shopInfo} clickout={clickout} intlFull={intlFull} />
            <SDPCertificates shopInfo={shopInfo} />
            <SDPPayment shopInfo={shopInfo} />
            {shopInfo.shop_intro || shopInfo.shop_details ? (
              <div className={styles.intro}>{parse(shopInfo.shop_intro || shopInfo.shop_details)}</div>
            ) : null}
            {hasReadMore && !openReadMore && (
              <div className={styles.readMore}>
                <p onClick={openHightLightOpen}>
                  <FormattedMessage id="shop.readMore" defaultMessage="Read more" />
                </p>{' '}
                <ArrowDownIcon onClick={openHightLightOpen} />
              </div>
            )}
          </div>
          <div className={classNames(styles.sidebarHeader, { [styles.fullSidebarHeader]: !!shopInfo?.company })}>
            {!!shopInfo.shop_logo_233_100 && (
              // Image dimensions: w=200, h=86
              <img
                alt={`${shopInfo.shop_name}'s logo`}
                src={shopInfo.shop_logo_233_100}
                title={`${shopInfo.shop_name}'s logo`}
                width="200"
                height="86"
                data-testid="ShopDetailsPageLogo"
              />
            )}
            {!!shopInfo?.company && (
              <div className={styles.legalShop}>
                <div className={styles.legalShopTitle}>
                  <InfoIcon /> <FormattedMessage id="shop.legal.details" defaultMessage="Details" />
                </div>
                <hr />
                <div className={styles.legalShopWrapper}>
                  {shopInfo?.company?.trade_name && (
                    <div className={styles.legalShopRow}>
                      <div className={classNames('col-6-xs', styles.legalShopField)}>
                        <FormattedMessage id="shop.legal.tradeName" defaultMessage="Trade name" />
                      </div>
                      <div className="col-6-xs">{shopInfo.company.trade_name}</div>
                    </div>
                  )}
                  {(shopInfo?.company?.legal_address ||
                    shopInfo?.company?.legal_city ||
                    shopInfo?.company?.legal_country) && (
                    <>
                      <div className={styles.legalShopRow}>
                        <div className={classNames('col-6-xs', styles.legalShopField)}>
                          <FormattedMessage id="shop.legal.businessAddress" defaultMessage="Business address" />
                        </div>
                        <div className="col-6-xs">
                          {shopInfo?.company?.legal_address ? shopInfo?.company?.legal_address + ' ' : ''}
                          {shopInfo?.company?.legal_city ? shopInfo?.company?.legal_city + ' ' : ''}
                          {shopInfo?.company?.legal_country ? shopInfo?.company?.legal_country : ''}
                        </div>
                      </div>
                      <SwitchableFeature feature={CART}>
                        <div className={styles.legalShopRow}>
                          <div className="col-6-xs"></div>
                          <div className="col-6-xs">
                            <FormattedMessage
                              id="shop.legal.businessNote"
                              values={{
                                shopName: shopInfo.shop_name,
                                a: (chunks) => (
                                  <a target="__blank" href={getPagePath(ARD_PROFILE_ORDERS_N_RETURNS)}>
                                    {chunks}
                                  </a>
                                ),
                              }}
                            />
                          </div>
                        </div>
                      </SwitchableFeature>
                    </>
                  )}
                  {shopInfo?.company?.registration_number && (
                    <div className={styles.legalShopRow}>
                      <div className={classNames('col-6-xs', styles.legalShopField)}>
                        <FormattedMessage id="shop.legal.chamberOfCommerce" defaultMessage="Chamber of Commerce" />
                      </div>
                      <div className="col-6-xs">{shopInfo.company.registration_number}</div>
                    </div>
                  )}
                  {shopInfo?.company?.vat_number && (
                    <div className={styles.legalShopRow}>
                      <div className={classNames('col-6-xs', styles.legalShopField)}>
                        <FormattedMessage id="shop.legal.VAT_number" defaultMessage="VAT number" />
                      </div>
                      <div className="col-6-xs">{shopInfo.company.vat_number}</div>
                    </div>
                  )}
                  {shopInfo?.company?.phone_number && (
                    <div className={styles.legalShopRow}>
                      <div className={classNames('col-6-xs', styles.legalShopField)}>
                        <FormattedMessage id="shop.legal.phoneNumber" defaultMessage="Phone number" />
                      </div>
                      <div className="col-6-xs">{shopInfo.company.phone_number}</div>
                    </div>
                  )}
                  {shopInfo?.company?.company_email && (
                    <div className={styles.legalShopRow}>
                      <div className={classNames('col-6-xs', styles.legalShopField)}>
                        <FormattedMessage id="shop.legal.email" defaultMessage="Email" />
                      </div>
                      <div className="col-6-xs">
                        <a href={`mailto:${shopInfo.company.company_email}`}>{shopInfo.company.company_email}</a>
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.legalShopOrderAndReturn}>
                  {/* <div className={styles.legalShopOrderAndReturnItem}>
                          <DeliveryIcon /> <FormattedMessage id="shop.legal.deliver" defaultMessage="Deliver" />:
                          <span className={styles.legalShopPolicy}>
                            <FormattedMessage id="shop.legal.standardDelivery" defaultMessage="Standard delivery" />
                          </span>
                        </div> */}
                  {!!shopInfo?.shopInfo && (
                    <div className={styles.legalShopOrderAndReturnItem}>
                      <ReturnIcon /> <FormattedMessage id="shop.legal.return" defaultMessage="Return" />:
                      <span className={styles.legalShopPolicy} onClick={() => setOpenReturnPolicy(true)}>
                        <FormattedMessage id="shop.legal.standardReturn" defaultMessage="Standard return policy" />
                      </span>
                      <ReturnPolicyModal isOpen={openReturnPolicy} closeModal={closeModal} />
                    </div>
                  )}
                </div>
              </div>
            )}
            {!!shopInfo?.certificates?.length && (
              <div className={styles.shopAccolades}>
                <h3 className={styles.accoladeHeading}>
                  <FormattedMessage id="shop.certificates" defaultMessage="Certificates" />
                </h3>
                {shopInfo.certificates.map((method, index) => {
                  return (
                    // Image dimensions: w=100, h=35
                    <img
                      src={method.image}
                      alt={method.name}
                      key={method + index}
                      title={method.name}
                      height="35"
                      width="100"
                    />
                  );
                })}
              </div>
            )}
            {!!shopInfo?.payment_methods?.length && (
              <div className={styles.shopPaymentMethods}>
                <h3 className={styles.accoladeHeading}>
                  <FormattedMessage id="shop.paymentMethod" defaultMessage="Betaalmethode" />
                </h3>
                {shopInfo.payment_methods.map((method, index) => {
                  return (
                    // Image dimensions: w=100, h=35
                    <img
                      src={method.image}
                      alt={method.name}
                      key={method + index}
                      title={method.name}
                      height="35"
                      width="100"
                    />
                  );
                })}
              </div>
            )}
          </div>
          <div className={styles.shopContent}>
            {!!topDiscounts?.length && (
              <div className={styles.topDeals}>
                <FormattedMessage
                  defaultMessage="Items on sale at the shop"
                  id="shop.topDealsHeader"
                  values={{
                    shopName: shopInfo.shop_name,
                    span: (chunks) => <span>{chunks}</span>,
                  }}
                  tagName="h2"
                />
                <div className={classNames('row', styles.productContainer)}>
                  {topDiscounts.map((product, index) => (
                    <div className={'col-6-xs col-4-m col-3-xxl'} key={`product_${product.id}`}>
                      <Product
                        discountPercent={product?.discount_pct}
                        adult={product?.adult}
                        brand={product.brand}
                        classList={{ root: styles.ProductDeal }}
                        currency={product.currency}
                        shortDesc={product.shortDesc}
                        EAN={product.offers[0].EAN}
                        price={product.price}
                        originalPrice={product.price_original || product.price}
                        id={product.id}
                        image={getCDNImage(product.thumbs[0])}
                        isSelected={false}
                        name={product.name}
                        onClick={() => navigate(product.canonical_path)}
                        shop={{ name: product.campaign }}
                        SKU={product.SKU}
                        showProductLink={false}
                        url={product.canonical_path}
                        view={VIEW_OPTIONS_TILES}
                        shopId={product?.shop_id}
                        merchantPid={product?.merchant_product_id}
                        resultRank={index}
                        category={product?.category}
                        productCardOriginType={PRODUCT_SHOP_DEALS}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!!shopInfo?.tops?.categories?.buckets?.length && (
              <TopCategories list={shopInfo?.tops?.categories?.buckets} shopName={shopInfo.shop_name} />
            )}
            {!!shopInfo?.products?.length && (
              <ShopProducts
                products={shopInfo?.products}
                shopName={shopInfo?.shop_name}
                shopRating={shopInfo.ratings?.rating}
              />
            )}
            {/* {!!shopInfo?.shop_gallery?.length && (
              <div className={classNames(styles.shopGallery, 'col-12-xs')}>
                <div className={styles.galleryContainer}>
                  {shopInfo.shop_gallery.map((image, index) => {
                    return (
                      // Image dimensions: w=346, h=260
                      <img
                        src={image.file}
                        className={styles.galleryImage}
                        key={image + index}
                        alt={image.alt}
                        title={image.alt}
                        height="260"
                        width="346"
                      />
                    );
                  })}
                </div>
                <ShopMasonary images={shopInfo.shop_gallery} />
              </div>
            )} */}
            {!!shopInfo?.shop_description && (
              <div className={classNames(styles.description, 'col-12-xs')}>{parse(shopInfo.shop_description)}</div>
            )}
            {!!shopInfo?.similar_shops?.length && (
              <div className={classNames(styles.similarShopsContainer, 'col-12-xs')}>
                <h2>
                  <FormattedMessage
                    id="shop.similarShops"
                    defaultMessage="<span>Similar</span> <span>shops</span>"
                    values={{ span: (chunks) => <span>{chunks}</span> }}
                  />
                </h2>
                <SimilarShops similarShops={shopInfo?.similar_shops} />
              </div>
            )}

            <ReviewsComponent
              profile={profile}
              openLoginModal={showOverlay}
              formId={`shopReview${shopWebsite}`}
              headerRenderer={() => (
                <>
                  <span>
                    <FormattedMessage id="shop.reviews" defaultMessage="Shop reviews" />{' '}
                  </span>
                  <FormattedMessage id="pagination.from" defaultMessage="from" /> {shopInfo.shop_name}
                </>
              )}
              averageRating={shopInfo?.comments?.length && shopInfo.ratings ? shopInfo.ratings.rating : 0}
              ratingCount={shopInfo?.comments?.length || 0}
              ratingMessageRenderer={(rating) => (
                <>
                  {!rating && <FormattedMessage id="reviews.noReviews" defaultMessage="There are no reviews yet" />}
                  {!!rating && (
                    <>
                      {rating} <FormattedMessage id="shop.reviews" defaultMessage="Shop reviews" />
                    </>
                  )}
                </>
              )}
              ratingCountsByScore={shopInfo?.ratings}
              comments={shopInfo?.comments}
              addReviewRequest={addReviewRequest}
              notShowFilter
            />
          </div>
          <div className={styles.relatedShop}>
            {!!shopInfo?.similar_shops?.length && (
              <div
                className={classNames(styles.similarShopsContainer, 'col-12-xs', styles.similarShopsContainerSidebar)}
              >
                <h2>
                  <FormattedMessage
                    id="shop.similarShops"
                    defaultMessage="<span>Similar</span> <span>shops</span>"
                    values={{ span: (chunks) => <span>{chunks}</span> }}
                  />
                </h2>
                <SimilarShops similarShops={shopInfo?.similar_shops} />
              </div>
            )}
          </div>
        </main>
      </div>
      <RecentlyViewedProducts />
      <NewsletterSection />
      <Footer />
      <LoginModal isOpen={openOverlay} setOpen={setOpenOverlay} preserveLoginInitURL />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: `{
    "@context": "https://schema.org",
    "@type": "Store",
    "@id": "${shopInfo.shop_website}",
    "name": "${shopInfo.shop_name}",
    "telephone": ${shopInfo.shop_phone?.replace(/(.+)/, '"$1"') || null},
    "email": ${shopInfo.shop_email?.replace(/(.+)/, '"$1"') || null},
    "image": "${shopInfo.shop_logo_233_100}",
    "brand": ${storeSchemaBrandsString},
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "${process.env.REACT_APP_DEFAULT_LANGUAGE_CODE?.toUpperCase()}",
      "streetAddress": ${shopInfo?.shop_map?.[0]?.address?.replace(/(.+)/, '"$1"') ||
        shopInfo?.shop_address?.replace(/(.+)/, '"$1"') ||
        null}
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": ${shopInfo?.shop_map?.[0]?.lat?.toString()?.replace(/(.+)/, '"$1"') || null},
      "longitude": ${shopInfo?.shop_map?.[0]?.lng?.toString()?.replace(/(.+)/, '"$1"') || null}
    }
    ${ratingMicrodata(shopInfo)}
  }`,
        }}
      />
    </>
  ) : null;
};

ShopDetailsPage.displayName = 'ShopDetailsPage';

export const mapStateToProps = ({ config, intl, profile, clickout }) => ({
  config,
  intl,
  profile,
  clickout,
});
export default connect(mapStateToProps)(ShopDetailsPage);
